import React, { useState } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { MoreOutlined } from '@ant-design/icons';
import AddTournamentModal from './AddTournamentModal';
import { EventDates } from './EventDetailsScreen';
import { Dropdown, Typography, Menu } from 'antd';
import {
  TEXT_SIZES,
  COLORS,
  DateFormatStrings,
  FONT_WEIGHTS,
  TOURNAMENT_TYPE_COLORS,
} from '../../../utils/constants';
import {
  buildDoublesBracketsPath,
  buildDoublesPlayersDetailsPath,
  buildSinglesBracketsPath,
  buildSinglesGenerateBracketPath,
  buildTeamsPlayersDetailsPath,
} from '../../../routes/routePaths';
import { TournamentTypeMap } from '../../../utils/eventUtils';
import { convertUtcToLocal } from '../../../utils/dateUtils';
import { EventDetails, TournamentSummary } from '../../../types/types';
import { TournamentStatus, TournamentType } from '../../../enums/enums';
import { useEventContext } from '../../../contexts/EventContext';
import TournamentEditModal from './TournamentEditModal';
import { useSnackbar } from '../../../contexts/SnackbarContext/SnackbarProvider';
import DeleteModal from '../../shared/DeleteModal';
import { deleteTournamentRequest } from '../../../api/requests/tournaments';
import { ScrollContainer } from './DoublesPlayersDetails';

interface CategoryLabelProps {
  tournamentTypeName?: string;
  tournamentCategory?: string;
  categoryChipColor?: string;
}

const CategoryLabel: React.FC<CategoryLabelProps> = ({
  tournamentTypeName,
  tournamentCategory,
  categoryChipColor,
}) => {
  const categoryLabel = tournamentCategory ?? 'Unknown Category';

  return (
    <CategoriesRow>
      {[tournamentTypeName, categoryLabel].map((label, idx) => {
        const isTypeChip = idx === 0;
        const chipColor = isTypeChip
          ? TOURNAMENT_TYPE_COLORS[tournamentTypeName as string] ||
            COLORS.grey.light
          : categoryChipColor || COLORS.grey.secondaryLight;
        const textColor = isTypeChip
          ? COLORS.white.primary
          : COLORS.black.primary;

        return (
          <CategoryChip key={idx} $backgroundColor={chipColor}>
            <CategoryText $color={textColor}>{label}</CategoryText>
          </CategoryChip>
        );
      })}
    </CategoriesRow>
  );
};

export const TournamentCard: React.FC<{
  tournament: TournamentSummary;
  eventDetails?: EventDetails;
  reloadEvent: () => Promise<void>;
}> = ({ tournament, eventDetails, reloadEvent }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const snackbar = useSnackbar();
  const [editOpen, setEditOpen] = useState<boolean>(false);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const { setSportName, setTournamentType } = useEventContext();

  const handleViewClick = (tournament: TournamentSummary) => {
    const isDraft = tournament.tournamentStatus === TournamentStatus.Draft;
    if (isDraft) return;
    setSportName(tournament.sportName ?? '');
    setTournamentType(
      TournamentTypeMap[
        tournament.tournamentType as keyof typeof TournamentTypeMap
      ] ?? '',
    );

    if (tournament.tournamentType === TournamentType.Singles) {
      if (tournament.bracketGenerated) {
        navigate(buildSinglesBracketsPath(tournament.tournamentId), {
          state: location.state,
        });
      } else {
        navigate(buildSinglesGenerateBracketPath(tournament.tournamentId), {
          state: location.state,
        });
      }
    } else {
      if (tournament.bracketGenerated) {
        navigate(buildDoublesBracketsPath(tournament.tournamentId), {
          state: location.state,
        });
      } else {
        if (tournament.tournamentType === TournamentType.Team) {
          navigate(buildTeamsPlayersDetailsPath(tournament.tournamentId), {
            state: location.state,
          });
        } else {
          navigate(buildDoublesPlayersDetailsPath(tournament.tournamentId), {
            state: location.state,
          });
        }
      }
    }
  };

  const menu = (
    <Menu
      items={[
        {
          key: 'edit',
          label: 'Edit Tournament',
          onClick: () => setEditOpen(true),
        },
        {
          key: 'delete',
          label: 'Delete Tournament',
          danger: true,
          onClick: () => setDeleteOpen(true),
        },
      ]}
    />
  );

  const handleDeleteTournament = async () => {
    try {
      const response = await deleteTournamentRequest(tournament.tournamentId);
      if (response?.isSuccess) {
        snackbar.success('Tournament deleted successfully');
        handleCancelDeleteTournament();
        await reloadEvent();
      } else {
        snackbar.error(response?.error || 'Error deleting tournament');
      }
    } catch (error: any) {
      snackbar.error(error?.Error || 'Error deleting tournament');
    }
  };

  const handleCancelDeleteTournament = () => {
    setDeleteOpen(false);
  };

  return (
    <>
      <Card>
        <RowContainer>
          {/* LEFT DETAILS */}
          <LeftDetails>
            <Sport>{tournament.sportName ?? tournament.tournamentName}</Sport>
            <CategoryLabel
              tournamentTypeName={tournament.tournamentTypeName || ''}
              tournamentCategory={`${tournament.customTournamentCategory}-${tournament.tournamentGenderName}`}
            />
            <EventDates>
              {tournament.startDate && tournament.endDate ? (
                <>
                  {convertUtcToLocal(
                    tournament.startDate,
                    DateFormatStrings.DD_MMM_YYYY,
                  )}{' '}
                  -{' '}
                  {convertUtcToLocal(
                    tournament.endDate,
                    DateFormatStrings.DD_MMM_YYYY,
                  )}
                </>
              ) : (
                '--'
              )}
            </EventDates>
            <FeeRow>
              Tournament Fee: <span>₹{tournament.entryFee}</span>
            </FeeRow>
          </LeftDetails>
          <RightSection>
            <PlayersRow>
              <PlayersCount>
                {tournament.participantCount ?? 0}
                <span>{tournament.isTeamTournament ? 'Teams' : 'Players'}</span>
              </PlayersCount>
              <Dropdown
                overlay={menu}
                trigger={['click']}
                placement="bottomRight"
              >
                <MoreIcon />
              </Dropdown>
            </PlayersRow>
            <ButtonRow>
              <Button
                disabled={
                  tournament.tournamentStatus === TournamentStatus.Draft
                }
                onClick={() => handleViewClick(tournament)}
              >
                {tournament.bracketGenerated ? 'View Matches' : 'View Details'}
              </Button>{' '}
            </ButtonRow>
          </RightSection>
        </RowContainer>
      </Card>
      <TournamentEditModal
        visible={editOpen}
        onClose={() => setEditOpen(false)}
        tournament={tournament}
        tournamentId={tournament.tournamentId as number}
        eventId={eventDetails?.eventId as number}
        eventStartDate={eventDetails?.eventStartDate}
        eventEndDate={eventDetails?.eventEndDate}
        eventRegistrationStartDate={eventDetails?.registrationStartDate ?? undefined}
        eventRegistrationEndDate={eventDetails?.registrationEndDate ?? undefined}
        reloadEvent={reloadEvent}
      />
      {deleteOpen && (
        <DeleteModal
          open={deleteOpen}
          moduleType="Tournament"
          onConfirm={handleDeleteTournament}
          onCancel={handleCancelDeleteTournament}
        />
      )}
    </>
  );
};

const Tournaments: React.FC<{
  tournaments: TournamentSummary[];
  total: number;
  eventDetails?: EventDetails;
  reloadEvent: () => Promise<void>;
}> = ({ tournaments, total, eventDetails, reloadEvent }) => {
  const [showAddModal, setShowAddModal] = useState(false);

  const handleAddSuccess = async () => {
    setShowAddModal(false);
    await reloadEvent();
  };

  return (
    <Wrapper>
      <HeaderRow>
        <Title>Tournaments ({total ?? tournaments?.length ?? 0})</Title>
        <AddButton onClick={() => setShowAddModal(true)}>
          + Add Tournament
        </AddButton>
      </HeaderRow>
      <ScrollContainer>
        <List>
          {tournaments?.map((t: TournamentSummary) => (
            <TournamentCard
              key={t.tournamentId}
              tournament={t}
              eventDetails={eventDetails}
              reloadEvent={reloadEvent}
            />
          ))}
        </List>
      </ScrollContainer>
      {showAddModal && eventDetails && (
        <AddTournamentModal
          visible={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSave={handleAddSuccess}
          eventId={eventDetails.eventId}
          eventStartDate={eventDetails?.eventStartDate}
          eventEndDate={eventDetails?.eventEndDate}
        />
      )}
    </Wrapper>
  );
};

export default Tournaments;

/* Styled */
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const Title = styled(Typography.Text)`
  font-weight: ${FONT_WEIGHTS.BOLD};
  font-size: ${TEXT_SIZES.MD}px;
`;

const AddButton = styled.button`
  padding: 8px 16px;
  background: ${COLORS.skyBlue.primary};
  color: ${COLORS.white.primary};
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-size: ${TEXT_SIZES.SM}px;
  &:hover {
    background: ${COLORS.skyBlue.primary};
  }
`;

const List = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  flex: 1;
  overflow-y: auto;
`;

const Card = styled.div`
  width: 100%;
  background: ${COLORS.grey.light};
  border: 0.5px solid ${COLORS.grey.lightMedium};
  border-radius: 16px;
  padding: 16px;
  display: flex;
`;

const RowContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`;

const LeftDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 70%;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 12px;
`;

const Button = styled.button`
  padding: 8px 16px;
  background: ${COLORS.white.primary};
  border: 1px solid ${COLORS.skyBlue.primary};
  border-radius: 8px;
  cursor: pointer;
  color: ${COLORS.skyBlue.primary};
  font-size: ${TEXT_SIZES.SM}px;
  white-space: nowrap;
  &:hover {
    background: ${COLORS.grey.secondaryLight};
  }
`;

const Sport = styled(Typography.Text)`
  font-weight: ${FONT_WEIGHTS.BOLD};
  font-size: ${TEXT_SIZES.MD}px;
  color: ${COLORS.black.primary};
`;

const Meta = styled.div`
  font-size: ${TEXT_SIZES.MD}px;
  font-weight: ${FONT_WEIGHTS.REGULAR};
  color: ${COLORS.grey.dark};

  span {
    font-weight: ${FONT_WEIGHTS.REGULAR};
    font-size: ${TEXT_SIZES.SM}px;
    color: ${COLORS.grey.medium};
    margin-left: 6px;
  }
`;

const FeeRow = styled.div`
  font-size: ${TEXT_SIZES.MD}px;
  font-weight: ${FONT_WEIGHTS.REGULAR};
  color: ${COLORS.grey.medium};

  span {
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    font-size: ${TEXT_SIZES.SM}px;
    color: ${COLORS.black.primary};
    margin-left: 6px;
  }
`;

const RightSection = styled.div`
  width: 30%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
`;

const PlayersCount = styled.div`
  font-size: ${TEXT_SIZES.MD}px;
  font-weight: ${FONT_WEIGHTS.BOLD};
  color: ${COLORS.black.primary};
  text-align: right;

  span {
    font-size: ${TEXT_SIZES.MD}px;
    font-weight: ${FONT_WEIGHTS.REGULAR};
    color: ${COLORS.grey.dark};
    margin-left: 4px;
  }
`;
const PlayersRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const MoreIcon = styled(MoreOutlined)`
  font-size: 20px;
  cursor: pointer;
  color: ${COLORS.grey.dark};

  &:hover {
    color: ${COLORS.skyBlue.primary};
  }
`;

const CategoriesRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const CategoryChip = styled.div<{ $backgroundColor: string }>`
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  padding: 4px 12px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const CategoryText = styled.span<{ $color: string }>`
  color: ${({ $color }) => $color};
  font-size: ${TEXT_SIZES.XS}px;
  font-weight: ${FONT_WEIGHTS.MEDIUM};
`;
